import { useState } from "react";
import { api, invoicePdfUrl, formatErr } from "@/lib/api";
import { money } from "@/lib/brands";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Scissors, Home, Stethoscope, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ICON = { grooming: Scissors, boarding: Home, consultation: Stethoscope };

export function CheckoutDialog({ patientId, trigger, onSettled }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setData(null);
    try {
      const { data } = await api.get(`/checkout/by-patient/${patientId}`);
      setData(data);
      const a = {};
      data.items.forEach((it) => (a[`${it.type}:${it.id}`] = it.suggested || ""));
      setAmounts(a);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const total = data ? data.items.reduce((s, it) => s + (parseFloat(amounts[`${it.type}:${it.id}`]) || 0), 0) : 0;

  const settle = async () => {
    setBusy(true);
    try {
      const items = data.items.map((it) => ({ type: it.type, id: it.id, amount: parseFloat(amounts[`${it.type}:${it.id}`]) || 0 }));
      const { data: inv } = await api.post("/checkout/settle", {
        brand: data.brand, phone: data.phone, owner_name: data.owner_name, items,
      });
      toast.success(`Settled · ${inv.invoice_no}`);
      window.open(invoicePdfUrl(inv.id), "_blank");
      setOpen(false);
      onSettled?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-primary text-2xl flex items-center gap-2"><Receipt className="h-5 w-5" />Customer Checkout</DialogTitle></DialogHeader>
        {!data ? <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <div>
            <div className="text-sm mb-3">
              <span className="font-semibold">{data.owner_name}</span> · {data.phone}
              <span className="text-muted-foreground"> · {data.pets.length} pet(s)</span>
            </div>
            {data.items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="checkout-empty">No open items for this customer — everything is settled. 🎉</div>
            ) : (
              <div className="space-y-2">
                {data.items.map((it) => {
                  const Icon = ICON[it.type] || Receipt;
                  const key = `${it.type}:${it.id}`;
                  return (
                    <div key={key} data-testid={`checkout-item-${it.id}`} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                      <span className="grid place-items-center h-9 w-9 rounded-lg bg-secondary text-secondary-foreground"><Icon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{it.pet_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground text-sm">₹</span>
                        <Input type="number" min="0" step="0.01" className="w-24 h-9"
                               data-testid={`checkout-amount-${it.id}`}
                               value={amounts[key]} onChange={(e) => setAmounts((s) => ({ ...s, [key]: e.target.value }))} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-border">
                  <span className="font-primary text-lg">Total</span>
                  <span className="font-primary text-2xl font-bold text-primary" data-testid="checkout-total">{money(total)}</span>
                </div>
                <Button onClick={settle} disabled={busy} className="w-full mt-2" data-testid="settle-checkout-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Settle &amp; Close · Generate Bill
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
